import React, { useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Switch,
  Link,
  CircularProgress,
} from '@mui/material'
import useCurrentTeacher from 'common/hooks/useCurrentTeacher'
import api from 'common/services/api'

function InfoCard({ image, text }: { image: string; text: string }) {
  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 1,
        py: 2,
        backgroundColor: '#F5F5F5',
        flex: '1',
      }}
    >
      <Box
        component='img'
        src={`${process.env.REACT_APP_IFRAME_IMAGES_URL}images/teacher/awakening/${image}.svg`}
      />
      <CardContent sx={{ px: 0, pb: '0 !important' }}>
        <Typography
          variant='subtitle2'
          component='h3'
          align='center'
          fontWeight='bold'
          sx={{ whiteSpace: 'pre-line' }}
        >
          {text}
        </Typography>
      </CardContent>
    </Card>
  )
}

function Awakening() {
  const { currentTeacher, updateTeacher } = useCurrentTeacher()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (currentTeacher) {
      setLoading(false)
    }
  }, [currentTeacher])

  const handleDeactivate = () => {
    updateTeacher('awakeningForNewStudents', false)
    api({
      url: '/api2/teacher/disable-awakening',
      method: 'POST',
      data: {
        enableAwakeningForNewStudents: false,
        tCode: currentTeacher.code,
        tSecret: currentTeacher.getSecret(),
      },
    })
      .then(() => {
        // success
      })
      .catch((error) => {
        console.log('error', error)
        updateTeacher('awakeningForNewStudents', true)
      })
  }

  const handleSignInIntoAwakeningApp = () => {
    const modalButton = window.parent.document.body.querySelector(
      '#awakeningSigninConfirmationPopupLink'
    ) as HTMLElement
    if (modalButton) {
      modalButton.click()
    }
  }

  return (
    <Box sx={{ margin: '0 auto', padding: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant='h4'>Introducing Awakening</Typography>
        <Box sx={{ flex: 1 }} />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {loading ? (
            <CircularProgress sx={{ mr: 1 }} size='1.25rem' />
          ) : (
            <>
              <Switch
                id='activated-checkbox'
                size='small'
                sx={{ marginRight: '8px' }}
                color='secondary'
                checked={!!currentTeacher?.awakeningForNewStudents}
                onClick={() => {
                  if (currentTeacher?.awakeningForNewStudents) {
                    handleDeactivate()
                  } else {
                    window.parent.postMessage(
                      { type: 'resetStepperModal' },
                      window.location.origin
                    )
                    const modalButton =
                      window.parent.document.body.querySelector(
                        '#awakeningPopupLink'
                      ) as HTMLElement
                    if (modalButton) {
                      modalButton.click()
                    }
                  }
                }}
              />
              <Typography
                variant='body1'
                component='label'
                htmlFor='activated-checkbox'
              >
                {currentTeacher?.awakeningForNewStudents
                  ? 'Activated'
                  : 'Deactivated'}
              </Typography>
            </>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          width: '100%',
          minHeight: 200,
          backgroundImage: `url('${process.env.REACT_APP_IFRAME_IMAGES_URL}images/teacher/awakening/awakening-banner-image.png')`,
          backgroundSize: 'contain',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          borderRadius: '8px',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'flex-end',
          p: 4,
        }}
      >
        {/* <Button
          variant='contained'
          color='secondary'
          size='large'
          onClick={handleSignInIntoAwakeningApp}
          sx={{ textTransform: 'capitalize' }}
        >
          Sign in into Awakening App
        </Button> */}
      </Box>

      <Box
        sx={{
          mt: 4,
          display: 'flex',
          gap: 2,
          alignItems: 'stretch',
          position: 'relative',
          minHeight: 400,
        }}
      >
        <Box sx={{ flex: '1 0', minWidth: '40%' }}>
          <Typography variant='h5' component='h2' sx={{ color: 'grey.700' }}>
            A Fun Way to Boost Math Fluency
          </Typography>
          <Typography sx={{ mt: 2 }} variant='body2'>
            Awakening is a fun, 3D world where students play curriculum-aligned
            games.
          </Typography>
          <Typography sx={{ mt: 2 }} variant='body2'>
            It&quot;s offered as a free and easy way to boost fluency after the
            essential daily, adaptive practices in XtraMath.
          </Typography>
          <Typography
            variant='h5'
            component='h2'
            sx={{ color: 'grey.700', my: 3 }}
          >
            No Cost. No Ads, Just Fun Learning
          </Typography>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'start', gap: 3 }}>
              <InfoCard image='chart' text={`Enhance\nStudent Learning`} />
              <InfoCard image='rocket' text={'Boost\nMotivation'} />
              <InfoCard image='confidence' text={'Build\nConfidence'} />
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            flex: 1,
            background: `url('${process.env.REACT_APP_IFRAME_IMAGES_URL}images/teacher/awakening/dragon.svg')`,
            backgroundSize: 'contain',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        />
        <Box sx={{ position: 'absolute', top: 0, right: 0 }}>
          <Stack spacing={1} sx={{ mt: 2, textAlign: 'left' }}>
            <Button
              component='a'
              href='https://awakening.legendsoflearning.com/play'
              target='_blank'
              variant='text'
              sx={{
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                color: '#1976D2',
              }}
              startIcon={
                <Box
                  component='img'
                  src={`${process.env.REACT_APP_IFRAME_IMAGES_URL}images/teacher/awakening/game.svg`}
                />
              }
            >
              TRY THIS GAME
            </Button>
            <Button
              component='a'
              href='https://www.legendsoflearning.com/awakening-teachers/'
              target='_blank'
              variant='text'
              sx={{
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                color: '#1976D2',
              }}
              startIcon={
                <Box
                  component='img'
                  src={`${process.env.REACT_APP_IFRAME_IMAGES_URL}images/teacher/awakening/info.svg`}
                />
              }
            >
              MORE INFO
            </Button>
            <Button
              variant='text'
              sx={{
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                color: '#1976D2',
              }}
              onClick={() => {
                try {
                  const termsOfServicePopup =
                    window.parent.document.body.querySelector(
                      '#termsOfServicePopup'
                    ) as HTMLElement
                  if (termsOfServicePopup) {
                    termsOfServicePopup.click()
                  }
                } catch (e) {
                  console.log(
                    'Cannot access parent - cross-origin restriction',
                    e
                  )
                }
              }}
              startIcon={
                <Box
                  component='img'
                  src={`${process.env.REACT_APP_IFRAME_IMAGES_URL}images/teacher/awakening/tos.svg`}
                />
              }
            >
              TERMS OF SERVICE
            </Button>
          </Stack>
        </Box>
      </Box>
      <Typography sx={{ mt: 4 }} variant='body2'>
        There are no new accounts to manage and no personal data is shared. You
        can enable or disable Awakening for a specific student or an entire
        class from their XtraMath setting.{' '}
        <Link
          href='https://www.legendsoflearning.com/awakening-teachers/'
          target='_blank'
          sx={{ color: '#1976D2' }}
        >
          Read more
        </Link>
      </Typography>
    </Box>
  )
}

export default Awakening
